import React from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { QUESTIONS } from "../../data/assessment";

function OptionRow({ option, selected, onSelect, qId }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      data-testid={`option-${qId}-${option.key}`}
      aria-pressed={selected}
      className="flex w-full items-center justify-between text-left font-body"
      style={{
        minHeight: 48,
        padding: 16,
        gap: 12,
        borderRadius: 8,
        border: `1px solid ${selected ? "var(--rtd-gold)" : "var(--rtd-border)"}`,
        backgroundColor: selected ? "var(--rtd-gold-tint)" : "var(--rtd-card)",
        color: selected ? "var(--rtd-text)" : "#374151",
        fontWeight: selected ? 700 : 400,
        fontSize: 14,
        lineHeight: 1.4,
        cursor: "pointer",
        transition:
          "border-color 150ms ease, background-color 150ms ease, color 150ms ease, font-weight 150ms ease",
      }}
      onMouseEnter={(e) => {
        if (!selected) {
          e.currentTarget.style.borderColor = "var(--rtd-gold)";
          e.currentTarget.style.backgroundColor = "var(--rtd-gold-tint)";
        }
      }}
      onMouseLeave={(e) => {
        if (!selected) {
          e.currentTarget.style.borderColor = "var(--rtd-border)";
          e.currentTarget.style.backgroundColor = "var(--rtd-card)";
        }
      }}
    >
      <span className="flex items-center gap-3">
        <span
          aria-hidden="true"
          style={{
            fontWeight: 700,
            fontSize: 12,
            color: selected ? "var(--rtd-gold)" : "var(--rtd-muted)",
            minWidth: 14,
          }}
        >
          {option.key}
        </span>
        <span>{option.label}</span>
      </span>

      <span
        aria-hidden="true"
        className="flex shrink-0 items-center justify-center"
        style={{
          width: 22,
          height: 22,
          borderRadius: "50%",
          backgroundColor: selected ? "var(--rtd-gold)" : "transparent",
          border: selected ? "none" : "1px solid var(--rtd-border)",
          transition: "background-color 150ms ease, transform 150ms ease",
          transform: selected ? "scale(1)" : "scale(0.9)",
        }}
      >
        {selected && <Check size={14} strokeWidth={3} color="#0B1929" />}
      </span>
    </button>
  );
}

function QuestionCard({ question, selectedKey, onSelect, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      data-testid={`question-card-${question.id}`}
      style={{
        backgroundColor: "var(--rtd-card)",
        border: `1px solid ${selectedKey ? "rgba(232,160,32,0.35)" : "var(--rtd-border)"}`,
        borderRadius: 16,
        padding: 28,
        transition: "border-color 200ms ease, box-shadow 200ms ease",
        boxShadow: selectedKey
          ? "0 4px 20px rgba(232,160,32,0.06)"
          : "0 1px 2px rgba(17,24,39,0.03)",
      }}
    >
      <div
        className="font-body"
        style={{
          fontWeight: 700,
          fontSize: 11,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "var(--rtd-muted)",
          marginBottom: 8,
        }}
      >
        {question.number}
      </div>
      <p
        className="font-body"
        style={{
          fontWeight: 700,
          fontSize: 16,
          color: "var(--rtd-text)",
          lineHeight: 1.45,
          marginBottom: 16,
        }}
      >
        {question.text}
      </p>
      <div className="flex flex-col" style={{ gap: 8 }}>
        {question.options.map((opt) => (
          <OptionRow
            key={opt.key}
            option={opt}
            qId={question.id}
            selected={selectedKey === opt.key}
            onSelect={() => onSelect(question.id, opt.key)}
          />
        ))}
      </div>
    </motion.div>
  );
}

export default function Scorecard({ answers, onSelect, answeredCount, onCalculate }) {
  const allAnswered = answeredCount === QUESTIONS.length;

  return (
    <section
      className="w-full"
      style={{ backgroundColor: "var(--rtd-bg)", paddingTop: 32, paddingBottom: 96 }}
      data-testid="scorecard-section"
      id="assessment"
    >
      <div className="mx-auto" style={{ maxWidth: 680, paddingLeft: 24, paddingRight: 24 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          style={{ marginBottom: 40 }}
        >
          <p className="eyebrow" style={{ marginBottom: 8 }}>
            The Assessment
          </p>
          <h2
            className="font-serif-display"
            style={{ fontSize: 32, color: "var(--rtd-text)", lineHeight: 1.15 }}
          >
            Your Patient Quality Score
          </h2>
          <p
            className="font-body"
            style={{ fontSize: 16, color: "var(--rtd-text-secondary)", marginTop: 8, lineHeight: 1.6 }}
          >
            Select the option that most accurately describes your clinic&rsquo;s current paid
            ads setup.
          </p>
        </motion.div>

        <div className="flex flex-col" style={{ gap: 20 }}>
          {QUESTIONS.map((q, i) => (
            <QuestionCard
              key={q.id}
              question={q}
              index={i}
              selectedKey={answers[q.id]}
              onSelect={onSelect}
            />
          ))}
        </div>

        {/* Counter + Calculate */}
        <div className="flex flex-col items-center" style={{ marginTop: 40 }}>
          <div
            className="font-body"
            style={{ fontSize: 13, color: "var(--rtd-text-secondary)", marginBottom: 16 }}
            data-testid="answered-counter"
          >
            <span style={{ fontWeight: 700, color: allAnswered ? "var(--rtd-gold)" : "var(--rtd-text)" }}>
              {answeredCount}
            </span>{" "}
            of {QUESTIONS.length} answered
          </div>

          <motion.button
            type="button"
            onClick={onCalculate}
            disabled={!allAnswered}
            data-testid="calculate-score-button"
            whileHover={allAnswered ? { scale: 1.02 } : {}}
            whileTap={allAnswered ? { scale: 0.98 } : {}}
            className="font-body w-full sm:w-auto"
            style={{
              maxWidth: 320,
              minWidth: 280,
              backgroundColor: "var(--rtd-gold)",
              color: "var(--rtd-navy)",
              fontWeight: 700,
              fontSize: 16,
              padding: "16px 32px",
              borderRadius: 8,
              border: "none",
              cursor: allAnswered ? "pointer" : "not-allowed",
              opacity: allAnswered ? 1 : 0.4,
              transition: "opacity 200ms ease",
              boxShadow: allAnswered ? "0 8px 24px rgba(232,160,32,0.28)" : "none",
            }}
          >
            Calculate my score →
          </motion.button>

          <p
            className="font-body"
            style={{ fontSize: 12, color: "var(--rtd-muted)", marginTop: 12 }}
            data-testid="calculate-note"
          >
            All 7 questions must be answered
          </p>
        </div>
      </div>
    </section>
  );
}
